import { Controller } from "react-hook-form";
import type { Control, FieldValues, Path } from "react-hook-form";
import { Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { FieldLabel } from "./FieldLabel";

interface Props<T extends FieldValues> extends Omit<DatePickerProps, "value" | "onChange"> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  helperText?: string;
  maxWidth?: number | string;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  return null;
}

export function LabeledDatePicker<T extends FieldValues>(
  props: Props<T>,
): React.ReactElement {
  const { name, control, label, required, helperText, maxWidth = 600, ...rest } = props;
  return (
    <Stack sx={{ maxWidth, width: "100%" }}>
      <FieldLabel label={label} required={required === true} htmlFor={name} />
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = fieldState.error !== undefined;
          const message = fieldState.error?.message ?? helperText;
          const value = toDate(field.value);
          return (
            <DatePicker
              {...rest}
              value={value}
              onChange={(next) => {
                field.onChange(next);
              }}
              format="dd MMM yyyy"
              slotProps={{
                textField: {
                  id: name,
                  fullWidth: true,
                  error: hasError,
                  helperText: message,
                  onBlur: field.onBlur,
                  inputRef: field.ref,
                  name: field.name,
                },
              }}
            />
          );
        }}
      />
    </Stack>
  );
}
